/* eslint-disable react/prop-types */
import '../../services/i18n';
import * as pageStyles from '../../styles/internal-page/internal-page.module.scss';
import React from 'react';
import { graphql, Slice } from 'gatsby';
import { useTranslation, getI18n } from 'react-i18next';
import {getImageAndAlt,
  getSEOInformations,
  mapSectionIntoMain,
  removeUselessWidgets
} from '../../utils/parsing';
import Content from '../../components/page_structure/Content';
import BottomLayout from '../../components/layout/templates/BottomLayout';
import ContentTop from '../../components/page_structure/ContentTop';
import ImageTextList from '../../components/page_structure/ImageTextList';
import CkEditor from '../../components/shared/ckeditor/CkEditor';
import AutoOverview from '../../components/page_structure/AutoOverview';
import ListCards from '../../components/page_structure/ListCards';
import ListImageTextInsides from '../../components/page_structure/ListImageTextInsides';
import {normalizeString, setupi18n} from '../../utils/utils';
import Seo from '../../seo/seo';
import {
  getRelatedArticles,
  getRelatedContents,
  getRelatedProducts
} from './util/product-util';
import FacultyDiapo from '../../components/page_structure/FacultyDiapo';
import TestimonieDiapo from '../../components/page_structure/TestimonieDiapo';
import TitleProduct from '../../components/shared/TitleProduct';
import InternalPageSimpleLayoutNew from '../../components/layout/templates/InternalPageSimpleLayoutNew';

export const Head = ({ data, pageContext, location }) => {
  const product = data.page;

  const {locale, generalSeo, websiteSeo, name } = pageContext;
  const {
    pageSeo,
    metaDescription,
  } = getSEOInformations(data);

  const cover = getImageAndAlt(product.hero_banner, locale);
  const canonicalLink = `https://www.sciencespo.fr${location.pathname}`;

  return (
    <Seo
      canonicalLink={canonicalLink}
      coverMedia={cover}
      generalSeo={generalSeo}
      language={locale}
      location={location}
      metaDescription={metaDescription}
      pageSeo={pageSeo}
      type={pageContext.pageType}
      website={name}
      websiteSeo={websiteSeo}
    />
  );
};


export default function ExEDProduct ({ data, pageContext, location}) {
  location; // to remove warning
  const {websiteRoot, breadcrumbs, locale} = pageContext;
  setupi18n(locale, getI18n());

  const product = data.page;
  const {t} = useTranslation();

  const relatedArticles = getRelatedArticles(data, websiteRoot);
  const relatedProgrammes = getRelatedProducts(data, websiteRoot);
  const relatedContents = getRelatedContents(data, websiteRoot);

  const getBlocType = (bloc) => {
    if (bloc['bloc_type']) {
      return `${bloc['__typename']}-${bloc['bloc_type']}`;
    }
    return bloc['__typename'];
  };

  const headerCover = product.hero_banner;
  const Block = ({content}) => (
    <>
      {
        content.bloc_type && content.bloc_type !== 'TEXT'
        &&
        <h2>{t(`product.anchor.${getBlocType(content)}`)}</h2>
      }
      <CkEditor html={content.content}
        lang={locale}
      />
    </>
  );

  const Comps = {
    'CmsExEdProductBlocComponentPartners': ImageTextList,
    'CmsExEdProductBlocComponentTestimonies': TestimonieDiapo,
    'CmsExEdProductBlocComponentBloc': Block,
    'CmsExEdProductBlocComponentSimilarProducts': null,
    'CmsExEdProductBlocComponentPedagogicTeam': FacultyDiapo,
  };

  const [newBottom] = React.useState(removeUselessWidgets(mapSectionIntoMain(product?.bottom)));

  return (
    <Slice alias={'internal-page-layout'}>
      <InternalPageSimpleLayoutNew
        bottomContent={
          <>
            <ListImageTextInsides content={{articles: relatedArticles}}/>
            <BottomLayout>
              {newBottom?.map((content, index) => {
                return (
                  <Content
                    elem={content}
                    fullWidth
                    key={'bottom-' + index}
                    lang={locale}
                    locale={locale}
                    siteURL={websiteRoot}
                  />
                );
              })}
            </BottomLayout>
          </>
        }
        filAriane={breadcrumbs}
        header={
          <ContentTop
            className={pageStyles.marginBetweenBlocs}
            cover={headerCover}
            locale={locale}
            siteURL={websiteRoot}
          />
        }
        related_column_name={t('product.info.column-related')}
        related_contents={relatedContents}
        url={websiteRoot}
      >
        {product?.title && (
          <TitleProduct
            contents={[product.format]}
            label={product.title}
          />
        )}
        {product?.presentation && (
          <CkEditor html={product.presentation}></CkEditor>
        )}
        <AutoOverview blocs={product.blocs}/>
        {product.blocs.map((content, index) => {
          const Component = Comps[content['__typename']];
          const id = normalizeString(t(`product.anchor.${getBlocType(content)}`)).replace(/ /g, '_').toLowerCase();
          if (!Component)
            return(<React.Fragment key={index + 'page_component'}/>);
          return (
            <React.Fragment key={index + 'page_component'}>
              {id !== 'text' &&
                  <div id={id}
                    key={index + 'page_anchor'}
                  />
              }
              <Component
                content={content}
                key={index + 'page_component'}
              />
            </React.Fragment>
          );
        })}
        {relatedProgrammes && <ListCards content={{cards: relatedProgrammes}}/>}
      </InternalPageSimpleLayoutNew>
    </Slice>);
}

export const query = graphql`
 query (
        $productId: String!, 
        $relatedArticleFilter: CmsPortalNewsItemFilterInput
    ) {
    page: cmsExEdProduct(id: { eq: $productId }) {
        id
        title
        locale
        product_type
        catalog
        slug
        animation_language
        presentation

        seo {
            ...ExEdSeoFragment
        }
        expertise {
            ...ExEdExpertiseFragment
        }
        format {
            ...ExEdFormatFragment
        }
        sessions {
            ...ExEdSessionFragment
        }
        hero_banner {
            ...ExEdMediaFragment
        }
        thumbnail {
            ...ExEdMediaFragment
        }
        contacts {
            ...ExEdContactFragment
        }
        related_content : right_column  {
            duration {
                ...ExEdComponentProductDurationFragment
            }
            prices {
                ...ExEdComponentProductPriceFragment
            }
            miscellaneous {
                ...ExEdComponentProductMiscellaneousFragment
            }
            info_meetings
        }

        blocs {
            ...ExEdProductBlocFragment
        }
        createdAt
        updatedAt
    }
      allCmsPortalNewsItem(
        limit: 4, 
        sort: {publishedAt: DESC}
        filter: $relatedArticleFilter
        ) {
        nodes {
          slug
          title
          locale
          picture : cover_media {
            ...MediaFragment
          }
        }
    }
  }
`;